import { FlyWireApi, FlyWireEmbedApi } from './flywire';

export enum PaymentDriver {
  TouchNet = 'touchnet',
  Authorizenet = 'authorizenet',
  CashNet = 'cashnet',
  Stripe = 'stripe', // Obsolete connector
  StripeOwned = 'stripe_owned',
  StripeConnect = 'stripe_connect',
  PaypalRest = 'paypal_rest',
  PaypalPro = 'paypal_pro',
  PayFlow = 'payflow',
  PaypalSb = 'paypal_sb',
  FlyWire = 'flywire',
  FlyWireEmbed = 'flywire_embed',
  TouchNetTLink = 'touchnet_tlink',
  WnePayment = 'wnepayment'
}

export enum GatewayType {
  ApiHosted = 'api-hosted', // Legacy type
  Hosted = 'hosted',
  SelfHosted = 'self-hosted'
}

export interface PaymentProvider {
  _id: string;
  name: string;
  description: string;
  driver: PaymentDriver;
  gateway_type: GatewayType;
}

export type PaymentProviderData<T> = T & { testMode: boolean };

export interface FlyWirePaymentProvider extends PaymentProvider {
  driver: PaymentDriver.FlyWire;
  data: PaymentProviderData<FlyWireApi.ElementFlyWireConfig>;
}

export interface PaypalPaymentProvider extends PaymentProvider {
  driver: PaymentDriver.PaypalSb;
  data: PaymentProviderData<{ clientId: string }>;
}

export interface StripeConnectedPaymentProvider extends PaymentProvider {
  driver: PaymentDriver.StripeConnect;
  data: PaymentProviderData<{
    business_profile_name?: string;
    email?: string;
    api_publishable_key: string;
    account_id?: string;
  }>;
}

export interface StripeOwnedPaymentProvider extends PaymentProvider {
  driver: PaymentDriver.StripeOwned;
  data: PaymentProviderData<{ api_publishable_key: string }>;
}

export interface TouchNetTLinkPaymentProvider extends PaymentProvider {
  driver: PaymentDriver.TouchNetTLink;
  data: {
    upay_url: string;
    upay_site_id: number;
    test_mode: boolean;
  };
}
export interface AnyPaymentProvider extends PaymentProvider {
  data: PaymentProviderData<any>;
}

export enum RelatedEntityType {
  ApplicationDeposit = 'application_deposit',
  ApplicationFee = 'application_fee',
  Event = 'event',
  Form = 'form'
}

export interface RelatedEntity {
  _id?: string;
  guid?: string;
  name: string;
  type: RelatedEntityType;
}

export interface RelatedEntities {
  application_deposits: RelatedEntity[];
  application_fees: RelatedEntity[];
  events: RelatedEntity[];
  forms: RelatedEntity[];
}

export type PaymentProviderExpanded =
  | FlyWirePaymentProvider
  | PaypalPaymentProvider
  | StripeConnectedPaymentProvider
  | StripeOwnedPaymentProvider
  | TouchNetTLinkPaymentProvider
  | AnyPaymentProvider;

export interface PaymentGatewayBase<T extends PaymentGateway = PaymentGateway> {
  id: string;
  name: string;
  description?: string;
  driver: PaymentDriver;
  gateway_type?: GatewayType;
  data: T;
  created_at: string;
  updated_at: string;
  related_entities?: RelatedEntities;
}

export type PaypalGatewayExpanded = Pick<PaypalPaymentProvider, 'driver'> & {
  clientId: string;
  enc_secret?: string;
  testMode: boolean;
};

export type StripeOwnedPaymentProviderExpanded = Pick<
  StripeOwnedPaymentProvider,
  'driver' | 'data'
> & {
  data: { api_secret_key: string };
};

export interface FlyWireGateway {
  recipient: string;
  env: FlyWireEmbedApi.FlyWireEmbedEnv;
}

export interface TouchNetGateway {
  test_mode: boolean;
  test_tlink_url: string;
  test_upay_url: string;
  test_upay_site_id: string;
  tlink_url: string;
  upay_url: string;
  upay_site_id: string;
}

export type PaymentGateway =
  | PaypalGatewayExpanded
  | StripeOwnedPaymentProviderExpanded
  | FlyWireGateway
  | TouchNetGateway
  | StripeConnectedPaymentProvider;

export interface PaymentGatewayDto extends PaymentProvider {
  name: string;
  description: string;
}

export interface PaymentGatewayRelatedEntitiesCount {
  forms_count: number;
  applications_count: number;
  deposits_count: number;
  events_count: number;
}
